/**
 * What counts as a reminder, shared by web and native.
 *
 * A reminder is an ordinary `EventSource` carrying an `okthx` blob rather than a
 * separate kind of thing, so every surface that treats one differently — the
 * grid chip, the detail panel, the edit-permission check — needs the same
 * question answered the same way. Kept here rather than copied per platform
 * because a one-sided change to it would ship green: each package tests only its
 * own copy.
 *
 * Presentation lives next door in `reminderChip.ts`; this file is only the
 * predicate.
 */

import type { OkthxSourceBlob } from "@/types/calendarEvent.ts";

/**
 * True if an okthx blob marks its event a reminder.
 *
 * Takes the blob rather than the event because the callers that need it most —
 * chip renderers and drag handlers — often hold only the extended props a
 * calendar library carried through, not a whole `CalendarEvent`.
 */
export function isReminderBlob(okthx: OkthxSourceBlob | undefined): boolean {
  return okthx?.kind === "reminder";
}

/** True if this event is an okthx reminder rather than a calendar meeting. */
export function isReminderEvent(event: { okthx?: OkthxSourceBlob }): boolean {
  return isReminderBlob(event.okthx);
}
